/**
 * XSS Protection Utilities
 *
 * Safely renders user-generated content to prevent XSS attacks.
 * Provides functions for safe display, input validation, and secure storage.
 *
 * Main Functions:
 * ├── safeRender() ← Configurable safe rendering with validation options
 * ├── safeRenderEmail() ← Email-specific rendering with format validation
 * ├── safeRenderUrl() ← URL-specific rendering with protocol validation
 * ├── SafeText ← React component for safe content display
 * ├── validateUserInput() ← Input validation with XSS pattern detection
 * └── secureStorage ← Secure localStorage wrapper
 */

import React from 'react';
import {
  createBlockedCharacterRegex,
  FIELD_SECURITY_CONFIGS,
} from '../constants/fieldValidation.constants';
import { HTML_ENTITIES } from '../constants/htmlEntities.constants';

// =============================================================================
// UTILITY FUNCTIONS - Internal helpers
// =============================================================================

/**
 * Converts dangerous characters to safe HTML entities
 */
export const escapeHtml = (text: string): string => {
  if (typeof text !== 'string') return '';

  return text.replace(/[&<>"'\/|(){}[\];:=\\%#+$`~^*]/g, (char) => {
    return HTML_ENTITIES[char as keyof typeof HTML_ENTITIES] || char;
  });
};

// =============================================================================
// CORE FUNCTIONS - Main XSS protection API
// =============================================================================

/**
 * Safely renders any user content with configurable validation
 *
 * Main function for safe content rendering with field-specific security
 * configurations and optional format validation and transformation.
 */
export const safeRender = (
  content: string | null | undefined,
  fieldType: keyof typeof FIELD_SECURITY_CONFIGS = 'TEXT',
  options: {
    validateFormat?: (content: string) => boolean;
    transform?: (content: string) => string;
  } = {},
): string => {
  if (!content || typeof content !== 'string') return '';

  const config = FIELD_SECURITY_CONFIGS[fieldType];

  // Check for malicious patterns
  const hasMaliciousPatterns = config.patterns.some((pattern: RegExp) =>
    pattern.test(content),
  );

  if (hasMaliciousPatterns) {
    console.warn(
      `XSS Protection: Malicious pattern detected in ${fieldType}, blocking content`,
    );
    return '[Content blocked for security]';
  }

  // Optional format validation
  if (options.validateFormat && !options.validateFormat(content)) {
    return '';
  }

  // Remove dangerous characters
  const blockedRegex = createBlockedCharacterRegex(config.blockedCharacters);
  let cleaned = content.replace(blockedRegex, '').trim();

  // Optional transformation
  if (options.transform) {
    cleaned = options.transform(cleaned);
  }

  return escapeHtml(cleaned);
};

/**
 * Safe email rendering with format validation
 */
export const safeRenderEmail = (email: string | null | undefined): string => {
  return safeRender(email, 'EMAIL', {
    validateFormat: (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
    transform: (email) => email.toLowerCase(),
  });
};

/**
 * Safe URL rendering with protocol validation
 */
export const safeRenderUrl = (url: string | null | undefined): string => {
  return safeRender(url, 'TEXT', {
    validateFormat: (url) => {
      // Allow data URLs for base64 images, blob URLs, and HTTP/HTTPS
      if (url.startsWith('data:image/') || url.startsWith('blob:')) {
        return true;
      }

      try {
        const urlObj = new URL(url);
        return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
      } catch {
        return false;
      }
    },
  });
};

// Convenience aliases for backward compatibility
export const safeRenderText = (text: string | null | undefined): string =>
  safeRender(text, 'TEXT');

export const safeRenderName = (name: string | null | undefined): string =>
  safeRender(name, 'NAME');

/**
 * React component for safe content rendering
 *
 * Provides a secure wrapper for displaying user content with automatic
 * HTML escaping and field-specific validation.
 */
export interface SafeTextProps {
  text: string | null | undefined;
  fieldType?: keyof typeof FIELD_SECURITY_CONFIGS;
  fallback?: string;
  maxLength?: number;
}

export const SafeText: React.FC<SafeTextProps> = ({
  text,
  fieldType = 'TEXT',
  fallback = '',
  maxLength,
}) => {
  let safeText = safeRender(text, fieldType);

  if (maxLength && safeText.length > maxLength) {
    safeText = safeText.slice(0, maxLength) + '...';
  }

  return React.createElement('span', {
    dangerouslySetInnerHTML: { __html: safeText || fallback },
  });
};

/**
 * Real-time input validation with XSS pattern detection
 *
 * Provides immediate feedback during user input to prevent submission
 * of malicious content. Uses centralized security constants for consistency.
 */
export const validateUserInput = (
  input: string,
  type: 'text' | 'email' | 'username' | 'name' = 'text',
): { isValid: boolean; sanitized: string; error?: string } => {
  // Empty fields should be valid (no security warning for empty)
  if (!input || typeof input !== 'string' || input.trim() === '') {
    return { isValid: true, sanitized: '', error: undefined };
  }

  // Get security config for this field type (all use MAXIMUM security)
  const configKey = type.toUpperCase() as keyof typeof FIELD_SECURITY_CONFIGS;
  const securityConfig =
    FIELD_SECURITY_CONFIGS[configKey] || FIELD_SECURITY_CONFIGS.TEXT;

  // 1. Check for dangerous characters using MAXIMUM security
  const blockedRegex = createBlockedCharacterRegex(
    securityConfig.blockedCharacters,
  );
  const hasDangerousChars = blockedRegex.test(input);

  // 2. Check for malicious patterns using comprehensive protection
  const hasMaliciousPatterns = securityConfig.patterns.some((pattern: RegExp) =>
    pattern.test(input),
  );

  if (hasDangerousChars || hasMaliciousPatterns) {
    return {
      isValid: false,
      sanitized: '',
      error: 'Input contains potentially dangerous content',
    };
  }

  let sanitized = '';

  switch (type) {
    case 'email':
      sanitized = safeRenderEmail(input);
      break;
    case 'name':
    case 'username':
      sanitized = safeRenderName(input);
      break;
    default:
      sanitized = safeRenderText(input);
  }

  return { isValid: true, sanitized };
};

/**
 * Secure localStorage wrapper
 *
 * localStorage is vulnerable to XSS attacks if malicious scripts can access
 * stored data. This wrapper ensures all stored and retrieved data is properly escaped.
 */
export const secureStorage = {
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(escapeHtml(key), escapeHtml(value));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  },

  getItem: (key: string): string | null => {
    try {
      const value = localStorage.getItem(escapeHtml(key));
      return value ? escapeHtml(value) : null;
    } catch (error) {
      console.error('Failed to read from localStorage:', error);
      return null;
    }
  },

  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(escapeHtml(key));
    } catch (error) {
      console.error('Failed to remove from localStorage:', error);
    }
  },
};
